import { Component } from '@theme/component';

/**
 * A custom element that displays a button to scroll back to the top of the page.
 * Becomes visible after the user scrolls past a configurable threshold and animates
 * the page back to the top, honoring `prefers-reduced-motion`.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The scroll-to-top trigger button.
 *
 * @extends {Component<Refs>}
 */
class ScrollToTopComponent extends Component {
  requiredRefs = ['button'];

  /** @type {number} */
  #threshold = 400;

  /** @type {number} */
  #ticking = 0;

  /** @type {boolean} */
  #visible = false;

  connectedCallback() {
    super.connectedCallback();

    const thresholdAttr = parseInt(this.getAttribute('threshold') ?? '', 10);
    if (!isNaN(thresholdAttr) && thresholdAttr > 0) {
      this.#threshold = thresholdAttr;
    }

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#updateVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('scroll', this.#onScroll);
    if (this.#ticking) cancelAnimationFrame(this.#ticking);
  }

  #onScroll = () => {
    if (this.#ticking) return;

    this.#ticking = requestAnimationFrame(() => {
      this.#updateVisibility();
      this.#ticking = 0;
    });
  };

  #updateVisibility() {
    const shouldBeVisible = window.scrollY > this.#threshold;

    if (shouldBeVisible === this.#visible) return;

    this.#visible = shouldBeVisible;
    this.classList.toggle('scroll-to-top--visible', shouldBeVisible);
    this.refs.button.setAttribute('aria-hidden', shouldBeVisible ? 'false' : 'true');
    this.refs.button.tabIndex = shouldBeVisible ? 0 : -1;
  }

  /**
   * Smoothly scrolls the window back to the top.
   * Bound via `on:click="/scrollToTop"` in the Liquid template.
   */
  scrollToTop = () => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };
}

if (!customElements.get('scroll-to-top')) {
  customElements.define('scroll-to-top', ScrollToTopComponent);
}
